
class CollectionFilterController extends HTMLElement {
  constructor() {
    super();

    this._sectionId = this.dataset.sectionId;
    this._checkboxes = [...this.querySelectorAll('input[type="checkbox"]')];
    this._rangeInputs = [...this.querySelectorAll('input[type="number"]')];
    this._activeLinks = [...this.querySelectorAll('.active_values_wrapper a')];
    this._sortSelect = document.querySelector('select[name="sort_by"]');
  }

  connectedCallback() {
    this.bindCheckboxFilters();
    this.bindPriceFilters();
    this.bindActiveRemovals();
  }

  /* -----------------------------
    CHECKBOX FILTERS
  ------------------------------ */
  bindCheckboxFilters() {
    if (!this._checkboxes.length) return;

    this._checkboxes.forEach(input => {
      input.addEventListener('change', () => {
        const url = this.resolveCheckboxUrl(input);
        this.processUpdate(url);
      });
    });
  }

  resolveCheckboxUrl(input) {
    const base = input.checked
      ? input.dataset.addUrl
      : input.dataset.removeUrl;

    const url = new URL(base, window.location.origin);
    url.searchParams.set('sections', this._sectionId);
    return url;
  }

  /* -----------------------------
    PRICE RANGE FILTERS
  ------------------------------ */
  bindPriceFilters() {
    if (!this._rangeInputs.length) return;

    this._rangeInputs.forEach(input => {
      input.addEventListener(
        'input',
        debounce(() => this.handlePriceChange(input), 1000)
      );
    });
  }

  handlePriceChange(changedInput) {
    const url = new URL(window.location.href);

    const minInput = this.findRangeInput(changedInput, 'gte');
    const maxInput = this.findRangeInput(changedInput, 'lte');

    this.applyRangeParam(url, minInput);
    this.applyRangeParam(url, maxInput);

    url.searchParams.set('sections', this._sectionId);
    this.processUpdate(url);
  }

  findRangeInput(origin, type) {
    const name = origin.name.includes(type)
      ? origin.name
      : origin.name.replace(type === 'gte' ? 'lte' : 'gte', type);

    return this.querySelector(`input[name="${name}"]`);
  }

  applyRangeParam(url, input) {
    if (!input) return;

    if (input.value) {
      url.searchParams.set(input.name, input.value);
    } else {
      url.searchParams.delete(input.name);
    }
  }

  /* -----------------------------
    ACTIVE FILTER REMOVAL
  ------------------------------ */
  bindActiveRemovals() {
    if (!this._activeLinks.length) return;

    this._activeLinks.forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('sections', this._sectionId);
        this.processUpdate(url);
      });
    });
  }

  /* -----------------------------
    CORE UPDATE PIPELINE
  ------------------------------ */
  processUpdate(url) {
    this.toggleLoader(true);

    this.fetchSection(url)
      .then(html => this.swapContent(html))
      .then(() => this.emitChange(url))
      .catch(err => console.error('[CollectionFilter]', err))
      .finally(() => {
        this.toggleLoader(false);
        this.updateHistory(url);
      });
  }

  async fetchSection(url) {
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error('Filter fetch failed');
    return response.json();
  }

  /* -----------------------------
    DOM REPLACEMENT
  ------------------------------ */
  swapContent(sectionData) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = sectionData[this._sectionId];

    this.replaceNode(
      '.collection_product ',
      wrapper
    );

    this.replaceNode(
      '.collection_products_count',
      wrapper
    );

    this.replaceNode(
      '.mobile_filter_drawer',
      wrapper
    );
  }

  swapContent(sectionData) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = sectionData[this._sectionId];

    this.replaceNode('.collection_product', wrapper);
    this.replaceNode('.collection_products_count', wrapper);
    this.replaceNode('.mobile_filter_drawer', wrapper);

    setTimeout(() => {
      document.dispatchEvent(new Event('DOMContentLoaded'));
    }, 100);
  }

  replaceNode(selector, source) {
    const incoming = source.querySelector(selector);
    const current = document.querySelector(selector);

    if (incoming && current) {
      current.replaceChildren(...incoming.childNodes);
    }
  }

  /* -----------------------------
    LOADER
  ------------------------------ */
  toggleLoader(active) {
    const loader = document.querySelector('.collection_product_grid_with_pagination .loader');
    if (loader) loader.classList.toggle('hidden', !active);
    if (this._sortSelect) this._sortSelect.disabled = active;
  }

  /* -----------------------------
    EVENTS + HISTORY
  ------------------------------ */
  emitChange(url) {
    document.dispatchEvent(
      new CustomEvent('collection-grid-update', {
        detail: {
          url,
          sectionId: this._sectionId
        }
      })
    );
  }

  updateHistory(url) {
    const clean = new URL(url.toString());
    clean.searchParams.delete('sections');
    window.history.pushState({}, '', clean);
  }
}

customElements.define('collection-filters', CollectionFilterController);

function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}
class SortingFilter extends HTMLElement {
  constructor() {
    super();

    this._formEl = this.querySelector('#main_coll_sorting_form');
    this._selectEl = this.querySelector('select[name="sort_by"]');
    this._sectionKey = this.dataset.sectionId;
  }

  connectedCallback() {
    if (!this._selectEl || !this._sectionKey) return;
 
    this._selectEl.addEventListener('change', this.handleChange.bind(this));
  } 

  handleChange() {
    const sortValue = this._selectEl.value; 

    this.toggleLoader(true);

    const requestUrl = this.buildUrl(sortValue);

    this.fetchSection(requestUrl)
      .then((markup) => this.swapGrid(markup))
      .then(() => this.emitUpdate(requestUrl))
      .catch((error) => console.error('[CollectionSort]', error))
      .finally(() => {
        this.toggleLoader(false);
        this.pushHistory(requestUrl);
      });
  }

  buildUrl(sortValue) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    url.searchParams.set('sections', this._sectionKey);
    return url;
  }

  async fetchSection(url) {
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error('Failed to load section');
    return response.json();
  }

  swapGrid(sectionData) {
    const container = document.createElement('div');
    container.innerHTML = sectionData[this._sectionKey];

    const incoming = container.querySelector('.collection_product');
    const current = document.querySelector('.collection_product');

    if (incoming && current) {
      current.replaceChildren(...incoming.childNodes);
    }
  }

  toggleLoader(active) {
    const loader = document.querySelector('.collection_product_grid_with_pagination .loader');

    if (loader) {
      loader.classList.toggle('hidden', !active);
    }

    if (this._selectEl) {
      this._selectEl.disabled = active;
    }
  }

  emitUpdate(url) {
    document.dispatchEvent(
      new CustomEvent('collection-grid-update', {
        detail: {
          url,
          sectionId: this._sectionKey
        }
      })
    );
  }

  pushHistory(url) {
    const cleanUrl = new URL(url.toString());
    cleanUrl.searchParams.delete('sections');
    window.history.pushState({}, '', cleanUrl);
  }
}

customElements.define('sorting-filer', SortingFilter);



class MobileFilters extends HTMLElement {
  constructor() {
    super();
    this.loader = document.querySelector('.collection_product_grid_with_pagination .loader');
    this.collectionGrid = document.querySelector('.collection_product');
    this.btn = this.querySelector('.mobile_filters_btn button');
    this.form = this.querySelector('form#mobile_filters_form');
    this.drawer = document.querySelector('.mobile_filter_drawer');
    this.activeValues = this.querySelectorAll('.mob_active_values_wrapper a');
    this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    this.attachListeners();
  }

  attachListeners() {
    if(this.btn) {
      this.btn.addEventListener('click', (e) => {
        e.preventDefault();
        if(!this.form || !this.collectionGrid) return;

        this.showLoader();
        this.submitForm();
      })
    }    

    if(this.activeValues.length) {
    this.activeValues.forEach((value) => {
      value.addEventListener('click', (e) => {
        e.preventDefault();

        this.showLoader();

        const url = new URL(e.currentTarget.getAttribute('href'), window.location.href);

        this.updateURL(url.toString()),
        this.updatePage(url.toString(), this.sectionId);

      })
    })
  }
  }

  showLoader() {
    const loader = document.querySelector('.collection_product_grid_with_pagination .loader');
    if(loader) {
      loader.classList.remove('hidden');
    }

    if(this.select) {
      this.select.disabled = true;
    }
  }

  hideLoader() {
    const loader = document.querySelector('.collection_product_grid_with_pagination .loader');
    if(loader) {
      loader.classList.add('hidden');
    }

    if(this.select) {
      this.select.disabled = false;
    }
  }

  async submitForm() {
    const formData = new FormData(this.form);

    const priceGte = this.form.querySelector('[name="filter.v.price.gte"]');
    const priceLte = this.form.querySelector('[name="filter.v.price.lte"]');

    const defaultMin = priceGte?.getAttribute("data-default");
    const defaultMax = priceLte?.getAttribute("data-default");

    if (priceGte && priceLte && defaultMin && defaultMax) {
      if (priceGte.value === defaultMin && priceLte.value === defaultMax) {      
        priceGte.value = "";
        priceLte.value = "";

        formData.delete("filter.v.price.gte");
        formData.delete("filter.v.price.lte");
      }
    }

    const params = new URLSearchParams();

    const currentParams = new URLSearchParams(window.location.search);
    const hasSortBy = formData.has('sort_by');

    for(const [key, value] of currentParams.entries()) {
      if(!key.startsWith('filter.v.') && !key.startsWith('filter.p.')) {
        if(key === 'sort_by' && hasSortBy) {
          continue;
        }
        params.append(key,  value);
      }
    }

    for(const [key, value] of formData.entries()) {
      if(value && value != "") {
        if(key === "sort_by") {
          params.set(key, value);
        } else {
          params.append(key, value);
        }
      }
    }

    const url = `${window.location.pathname}?${params.toString()}`;

    await this.updateURL(url);
    await this.updatePage(url, this.sectionId);
  }  

  async updateURL(url) {
    history.pushState(null, "", url);
  }

  async updatePage(url, id) {
    const newURL = url.includes('?')
  ? `${url}&sections=${id}`
  : `${url}?sections=${id}`;
    
    fetch(newURL)
    .then((res) => res.json())
    .then((htmlString) => {

      const temp = document.createElement('div');
      temp.innerHTML = htmlString[id];

      const newWrapper = temp.querySelector('.collection_product');
      const oldWrapper = document.querySelector('.collection_product');

      if(oldWrapper && newWrapper) {
        oldWrapper.innerHTML = newWrapper.innerHTML
      }

      const newCount = temp.querySelector('.collection_products_count');
      const oldCount = document.querySelector('.collection_products_count');

      if(oldCount && newCount) {
        oldCount.innerHTML = newCount.innerHTML;
      }

      const newMobileDrawer = temp.querySelector('.mobile_filter_drawer');
      const oldMobileDrawer = this.drawer;

      if(oldMobileDrawer && newMobileDrawer) {
        oldMobileDrawer.innerHTML = newMobileDrawer.innerHTML;
      }

      document.dispatchEvent(
        new CustomEvent('collection-grid-update', {
          detail: {
            url: url,
            sectionId: this.sectionId
          }
        })
      )

    })
    .finally(() => {
       const drawer = document.getElementById('mobile_filters');
       if (drawer && typeof drawer.close === 'function') drawer.close();
     //closeDrawer(this.drawer);
    })
  }

}

customElements.define('mobile-filters', MobileFilters);



class GlobalDrawer extends HTMLElement {
  constructor() {
    super();
    this.lastFocused = null;
  }

  connectedCallback() {
    this.drawer = this.querySelector('.global_drawer_wrap');
    if (!this.drawer) return;

    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');

    this.bindCloseButtons();
    this.bindBackdrop();
  }

  open(triggerBtn) {
    document.querySelectorAll('app-drawer').forEach(d => {
      if (d !== this) d.close();
    });

    this.lastFocused = triggerBtn || document.activeElement;

    this.drawer.classList.add('active');
    this.drawer.removeAttribute('inert');
    this.drawer.setAttribute('aria-hidden', 'false');

    this.enableFocus();
    this.focusFirst();

    document.body.classList.add('overflow-hidden');
    this.lockScroll();

    if (window.lenis) lenis.stop();

    this.handleKeydown = e => this.onKeydown(e);
    document.addEventListener('keydown', this.handleKeydown);
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('active');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');

    this.disableFocus();

    document.body.classList.remove('overflow-hidden');
    this.unlockScroll();

    if (window.lenis) lenis.start();

    document.removeEventListener('keydown', this.handleKeydown);

    this.lastFocused?.focus();
    this.lastFocused = null;
  }

  /* ---------------- FOCUS ---------------- */
  getFocusable() {
    return this.drawer.querySelectorAll(
      'button, a, input:not([type="hidden"]), select, textarea'
    );
  }

  enableFocus() {
    this.getFocusable().forEach(el => el.setAttribute('tabindex', '0'));
  }

  disableFocus() {
    this.getFocusable().forEach(el => el.setAttribute('tabindex', '-1'));
  }

  focusFirst() {
    const first = this.getFocusable()[0];
    first && first.focus();
  }

  trapFocus(e) {
    if (e.key !== 'Tab') return;

    const items = [...this.getFocusable()];
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------------- EVENTS ---------------- */
  onKeydown(e) {
    if (e.key === 'Escape') this.close();
    this.trapFocus(e);
  }

  bindBackdrop() {
    this.drawer.addEventListener('click', e => {
      if (e.target === this.drawer) this.close();
    });
  }

  bindCloseButtons() {
    this.querySelectorAll('.drawer_close_btn').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
  }

  /* ---------------- SCROLL ---------------- */
  lockScroll() {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px';
  }

  unlockScroll() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
}

customElements.define('global-drawer', GlobalDrawer);
class DrawerTrigger extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.drawerId = this.getAttribute('drawer');
    if (!this.drawerId) return;

    this.trigger = this.firstElementChild;
    if (!this.trigger) return;

    this.trigger.addEventListener('click', e => {
      e.preventDefault();

      const drawer = document.getElementById(this.drawerId);
      if (!drawer || typeof drawer.open !== 'function') return;

      drawer.open(this.trigger);
    });
  }
}

customElements.define('drawer-trigger', DrawerTrigger);
